// The customer record's data layer (Module 09, P3-E2 PR E2-2a1, D-323 rulings
// 1 to 7; spec 09 §5's E2-2 rows 1 to 9 and 22a). Notes and the pinned note,
// the tenant's tag list, and a customer's tags, over the generated client
// (doctrine 3). Every mutation sends an Idempotency-Key and every read threads
// `{signal}`, as in `sales-queries.ts`.
//
// TWO KINDS OF WRITE LIVE HERE.
//
//   * The hooks (`useNoteVerb`, `useCustomerTagVerb`, ...) are the single
//     verbs a screen presses once. Each mints a fresh key per press, as every
//     other sales mutation does. Two take the key from their caller instead:
//     `useAppendNote` and `useCreateTag`, whose forms keep the reader's text
//     after a failure and invite a second press, so the form holds one key
//     per attempt (`lib/attempt-key.ts`) and a resend after a lost response
//     is replayed rather than written twice.
//   * The bare senders (`attachCustomerTag`, `detachCustomerTag`,
//     `assignCustomerOwner`, `archiveCustomer`) take the key from their
//     caller. They are what `lib/bulk.ts` fans out over the selected rows,
//     and the bulk run owns the key: one per (run, row), reused when Retry
//     failed resends the row, so the server's idempotency layer replays the
//     first request's answer instead of writing twice (spec 09 §4.1's E2-2a1
//     annotation). The bulk run invalidates the book once when it ends,
//     rather than once per row.
//
// THE TAG LIST IS READ IN TWO SHAPES (spec 09 §4.1 and §4.13).
//
//   * The pickers and the book's Tag filter read `archived=in_use` at
//     `limit=200` and read on while `next_cursor` is set, so the filter offers
//     every active tag and every archived tag an active customer still
//     carries. The tenant holds at most 200 active tags, so the walk is one
//     page in practice and a bounded handful at worst.
//   * The Settings card reads `archived=none` fifty at a time, or `all` once
//     the reader asks for the archived ones, with Show more while the server
//     says more exist, because archived tags are never deleted and every
//     harness run adds some.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";
import { salesKeys } from "./sales-queries";
import { quoteKeys } from "./quote-queries";

type S = components["schemas"];

/** One note. The generated schema marks `CustomerNote` nullable at its
 * definition, because the 360 serves `pinned_note: CustomerNote.nullable()`
 * and the generator folds that `null` into the named schema, as it does for
 * `SalesNextActivity`; a note itself is never null. */
export type CustomerNote = NonNullable<S["CustomerNote"]>;
export type CustomerNoteListResponse = S["CustomerNoteListResponse"];
export type SalesTag = S["SalesTag"];
export type SalesTagArchivedFilter = S["SalesTagArchivedFilter"];
export type CustomerTagRef = S["CustomerTagRef"];

/** The Settings card's page size (spec 09 §4.13: fifty at a time). */
export const TAG_CATALOG_PAGE = 50;
/** The pickers' page size (spec 09 §4.1: `limit=200`). */
export const TAG_OPTIONS_PAGE = 200;
/** A walk that has not ended after this many pages is a server that never
 * stops naming a cursor; the read fails rather than looping. 200 × 50 is far
 * past the 200 active tags the tenant may hold plus every archived one an
 * active customer can carry. */
const TAG_OPTIONS_MAX_PAGES = 50;
/** The note timeline's page size; the route's default, said out loud. */
export const NOTES_PAGE = 50;

export const crmKeys = {
  notes: (customerId: string) => ["sales", "crm", "notes", customerId] as const,
  tagLists: () => ["sales", "crm", "tags"] as const,
  tagOptions: () => ["sales", "crm", "tags", "options"] as const,
  tagCatalog: (archived: SalesTagArchivedFilter) =>
    ["sales", "crm", "tags", "catalog", archived] as const,
};

// ---------------------------------------------------------------------------
// notes (spec 09 §5's E2-2 rows 1 to 3a)
// ---------------------------------------------------------------------------

/** The notes timeline, the pinned note first and then the newest, a page at a
 * time. LAZY like the Overview panels beside it: the 360's one GET carries the
 * pinned note for the header, and this read is the panel's alone. */
export function useCustomerNotes(customerId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: crmKeys.notes(customerId),
    enabled: enabled && customerId.length > 0,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) => {
      const res = await client.GET("/v1/customers/{id}/notes", {
        signal,
        params: {
          path: { id: customerId },
          query: { limit: NOTES_PAGE, ...(pageParam !== undefined ? { cursor: pageParam } : {}) },
        },
      });
      return unwrap(res, "Could not load the notes");
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** A note write moves the timeline, the 360's header (its pinned note) and
 * the book's rows (the `notes` alias rides on every list item). */
function invalidateNotes(qc: ReturnType<typeof useQueryClient>, customerId: string): void {
  void qc.invalidateQueries({ queryKey: crmKeys.notes(customerId) });
  void qc.invalidateQueries({ queryKey: salesKeys.customer(customerId) });
  void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
}

export function useAppendNote(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { body: string; pin: boolean; key: string }) => {
      const res = await client.POST("/v1/customers/{id}/notes", {
        params: { path: { id: customerId }, header: { "Idempotency-Key": body.key } },
        body: { body: body.body, ...(body.pin ? { pin: true } : {}) },
      });
      return unwrap(res, "Could not add the note");
    },
    onSuccess: () => {
      invalidateNotes(qc, customerId);
    },
  });
}

/** Pin, unpin and redact share one shape: a POST on the note with no body. */
type NoteVerb = "pin" | "unpin" | "redact";

/** Nothing reads the answer: a replay of a note write carries no `body`
 * (spec 09 §2.26, D-339), so the panel reads the timeline again instead. */
async function postNoteVerb(verb: NoteVerb, noteId: string): Promise<void> {
  const init = {
    params: { path: { id: noteId }, header: { "Idempotency-Key": newIdempotencyKey() } },
  };
  const res =
    verb === "pin"
      ? await client.POST("/v1/customer-notes/{id}/pin", init)
      : verb === "unpin"
        ? await client.POST("/v1/customer-notes/{id}/unpin", init)
        : await client.POST("/v1/customer-notes/{id}/redact", init);
  unwrap(res, "Could not change the note");
}

/** One mutation for the three note verbs, so the panel keeps one pending
 * state and one failure line: at most one note write is in flight, and the
 * failure on the glass is always the latest press's. */
export function useNoteVerb(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { verb: NoteVerb; noteId: string }) =>
      postNoteVerb(input.verb, input.noteId),
    onSuccess: () => {
      invalidateNotes(qc, customerId);
    },
  });
}

// ---------------------------------------------------------------------------
// the tag list (spec 09 §5's E2-2 rows 4 to 7)
// ---------------------------------------------------------------------------

async function fetchTagsPage(
  archived: SalesTagArchivedFilter,
  limit: number,
  cursor: string | undefined,
  signal?: AbortSignal,
) {
  const res = await client.GET("/v1/sales-tags", {
    ...(signal !== undefined ? { signal } : {}),
    params: { query: { archived, limit, ...(cursor !== undefined ? { cursor } : {}) } },
  });
  return unwrap(res, "Could not load the tags");
}

/** Every active tag and every archived tag an active customer carries, read
 * on while the server names a next page (spec 09 §4.1's Tag filter). */
export async function fetchTagOptions(signal?: AbortSignal): Promise<readonly SalesTag[]> {
  const out: SalesTag[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < TAG_OPTIONS_MAX_PAGES; page += 1) {
    const answer = await fetchTagsPage("in_use", TAG_OPTIONS_PAGE, cursor, signal);
    out.push(...answer.items);
    // A page that names no next cursor is the last one. A missing key is read
    // as null, so a malformed answer ends the walk rather than repeating it.
    const next = answer.next_cursor ?? null;
    if (next === null) return out;
    cursor = next;
  }
  throw new Error("The tag list did not end");
}

/** The pickers' and the Tag filter's read. `enabled` carries the role
 * mirror: a lineage the book refuses is refused this read too (§5's E2-2 row
 * 4), and a read the mirror knows will 403 is never sent (D-088). */
export function useTagOptions(enabled: boolean) {
  return useQuery({
    queryKey: crmKeys.tagOptions(),
    enabled,
    staleTime: 60_000,
    queryFn: async ({ signal }) => fetchTagOptions(signal),
  });
}

/** One tag's name, for a tag id the `in_use` read cannot name: an archived
 * tag that only archived customers carry, which still filters through a saved
 * view or a link (spec 09 §9.43). No route reads one tag, so this walks
 * `archived=all` until the id appears or the list ends, and answers null when
 * it never does. The customer export waits on it, so a file never names a
 * filter by a raw id (spec 09 §4.1). */
export async function fetchTagName(tagId: string, signal?: AbortSignal): Promise<string | null> {
  let cursor: string | undefined;
  for (let page = 0; page < TAG_OPTIONS_MAX_PAGES; page += 1) {
    const answer = await fetchTagsPage("all", TAG_OPTIONS_PAGE, cursor, signal);
    const found = answer.items.find((tag) => tag.id === tagId);
    if (found !== undefined) return found.name;
    const next = answer.next_cursor ?? null;
    if (next === null) return null;
    cursor = next;
  }
  return null;
}

export function useTagName(tagId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...crmKeys.tagLists(), "name", tagId ?? ""] as const,
    enabled: enabled && tagId !== undefined,
    staleTime: 60_000,
    queryFn: async ({ signal }) => fetchTagName(tagId ?? "", signal),
  });
}

/** The Settings card's read, fifty at a time with Show more. */
export function useTagCatalog(archived: SalesTagArchivedFilter, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: crmKeys.tagCatalog(archived),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, signal }) =>
      fetchTagsPage(archived, TAG_CATALOG_PAGE, pageParam, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** A tag write moves every tag read, and the chips the book and the 360 paint
 * (a rename changes a chip's words, an archive its status). */
function invalidateTags(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: crmKeys.tagLists() });
  void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
  void qc.invalidateQueries({ queryKey: ["sales", "customer"] });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; key: string }) => {
      const res = await client.POST("/v1/sales-tags", {
        params: { header: { "Idempotency-Key": input.key } },
        body: { name: input.name },
      });
      return unwrap(res, "Could not create the tag");
    },
    onSuccess: () => {
      invalidateTags(qc);
    },
  });
}

export function useRenameTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string }) => {
      const res = await client.PATCH("/v1/sales-tags/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { name: input.name },
      });
      return unwrap(res, "Could not rename the tag");
    },
    onSuccess: () => {
      invalidateTags(qc);
    },
  });
}

export function useArchiveTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/sales-tags/{id}/archive", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not archive the tag");
    },
    onSuccess: () => {
      invalidateTags(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// a customer's tags, and the bulk bar's four senders (rows 8, 9, 22a, archive)
// ---------------------------------------------------------------------------

/** `POST /v1/customers/{id}/tags` under the caller's key. */
export async function attachCustomerTag(customerId: string, tagId: string, key: string) {
  const res = await client.POST("/v1/customers/{id}/tags", {
    params: { path: { id: customerId }, header: { "Idempotency-Key": key } },
    body: { tag_id: tagId },
  });
  return unwrap(res, "Could not tag the customer");
}

/** `DELETE /v1/customers/{id}/tags/{tagId}` under the caller's key. */
export async function detachCustomerTag(customerId: string, tagId: string, key: string) {
  const res = await client.DELETE("/v1/customers/{id}/tags/{tagId}", {
    params: { path: { id: customerId, tagId }, header: { "Idempotency-Key": key } },
  });
  return unwrap(res, "Could not untag the customer");
}

/** Reassign the owner (D-326 ruling 3): `role: owner` with the owner the
 * caller saw as `expected_owner_user_id`, so a hand-over another admin made
 * meanwhile answers 409 `OWNER_CHANGED` instead of being overwritten. */
export async function assignCustomerOwner(
  customerId: string,
  userId: string,
  expectedOwnerUserId: string | null,
  key: string,
) {
  const res = await client.POST("/v1/customers/{id}/assignments", {
    params: { path: { id: customerId }, header: { "Idempotency-Key": key } },
    body: { user_id: userId, role: "owner", expected_owner_user_id: expectedOwnerUserId },
  });
  return unwrap(res, "Could not reassign the owner");
}

/** `POST /v1/customers/{id}/archive` under the caller's key. */
export async function archiveCustomer(customerId: string, key: string) {
  const res = await client.POST("/v1/customers/{id}/archive", {
    params: { path: { id: customerId }, header: { "Idempotency-Key": key } },
  });
  return unwrap(res, "Could not archive the customer");
}

/** What a bulk run's writes may have moved: the book, every 360 it touched,
 * and the quote book, whose owner filter re-buckets on a hand-over. */
export function useInvalidateAfterBulk() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
    void qc.invalidateQueries({ queryKey: ["sales", "customer"] });
    void qc.invalidateQueries({ queryKey: quoteKeys.quoteLists() });
    void qc.invalidateQueries({ queryKey: crmKeys.tagOptions() });
  };
}

/** The 360's single tag verbs: one press, one fresh key. Both answer the
 * customer's whole tag set, and both refresh the 360 and the book. */
export function useCustomerTagVerb(customerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { verb: "attach" | "detach"; tagId: string }) =>
      input.verb === "attach"
        ? attachCustomerTag(customerId, input.tagId, newIdempotencyKey())
        : detachCustomerTag(customerId, input.tagId, newIdempotencyKey()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: salesKeys.customer(customerId) });
      void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
      void qc.invalidateQueries({ queryKey: crmKeys.tagOptions() });
    },
  });
}
